import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Zap, ExternalLink, Check, X, Copy, Send, ChevronRight,
  Circle, Shield, Eye, EyeOff, Loader2, AlertTriangle,
} from "lucide-react";

// SVG-free brand accents — we key off "logo" strings from the API.
const LOGO_TEXT = {
  google: "G",
  meta: "M",
  tiktok: "T",
  snap: "S",
  webhook: "◉",
};

function StatusPill({ status, error }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-ink/40">
        <Circle size={8} /> No events
      </span>
    );
  }
  if (status === "success") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-600">
        <Check size={10} /> Healthy
      </span>
    );
  }
  return (
    <span
      title={error || ""}
      className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-rose-600"
    >
      <X size={10} /> Error
    </span>
  );
}

function ProviderCard({ p, onOpen }) {
  return (
    <button
      type="button"
      data-testid={`integration-card-${p.provider}`}
      onClick={() => onOpen(p.provider)}
      className="group text-start p-6 rounded-2xl border border-black/10 bg-white hover:border-electric hover:-translate-y-0.5 transition-all"
    >
      <div className="flex items-start justify-between">
        <div
          className="w-12 h-12 rounded-xl grid place-items-center text-white font-display font-black text-lg"
          style={{ backgroundColor: p.color }}
        >
          {LOGO_TEXT[p.logo] || p.name.charAt(0)}
        </div>
        <div className="flex items-center gap-2">
          {p.enabled ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-electric/10 text-electric text-[10px] font-mono uppercase tracking-widest">
              <Check size={10} /> On
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/5 text-ink/50 text-[10px] font-mono uppercase tracking-widest">
              Off
            </span>
          )}
        </div>
      </div>
      <div className="mt-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink/40">
          {p.category}
        </div>
        <div className="mt-1 font-display font-bold text-lg text-ink">
          {p.name}
        </div>
        <p className="mt-2 text-sm text-ink/60 leading-relaxed line-clamp-3">
          {p.description}
        </p>
      </div>
      <div className="mt-5 flex items-center justify-between">
        <StatusPill status={p.last_status} error={p.last_error} />
        <ChevronRight size={16} className="text-ink/30 group-hover:text-electric transition-colors" />
      </div>
    </button>
  );
}

function ConfigDrawer({ provider, onClose, onSaved }) {
  const [enabled, setEnabled] = useState(false);
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [events, setEvents] = useState([]);
  const [revealed, setRevealed] = useState({});

  useEffect(() => {
    if (!provider) return;
    setEnabled(provider.enabled);
    setConfig(provider.config || {});
    api.get(`/admin/integrations/${provider.provider}/events`)
      .then((r) => setEvents(r.data || []))
      .catch(() => setEvents([]));
  }, [provider]);

  const save = async () => {
    setSaving(true);
    try {
      // Never send back the read-only "computed" fields
      const cleaned = {};
      for (const f of provider.fields) {
        if (f.type === "readonly") continue;
        const v = config[f.key];
        if (v !== undefined) cleaned[f.key] = v;
      }
      await api.put(`/admin/integrations/${provider.provider}`, {
        enabled, config: cleaned,
      });
      toast.success(`${provider.name} saved`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(
        `/admin/integrations/${provider.provider}/test`
      );
      if (data.ok) toast.success(`Test OK · ${data.detail}`);
      else toast.error(`Test failed · ${data.detail}`);
      const evs = await api.get(`/admin/integrations/${provider.provider}/events`);
      setEvents(evs.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const copy = (v) => {
    if (!v) return;
    navigator.clipboard.writeText(v);
    toast.success("Copied");
  };

  if (!provider) return null;

  return (
    <div
      data-testid="integration-drawer"
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white border-b border-black/10 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl grid place-items-center text-white font-display font-black"
              style={{ backgroundColor: provider.color }}
            >
              {LOGO_TEXT[provider.logo] || provider.name.charAt(0)}
            </div>
            <div>
              <div className="font-display font-bold text-lg text-ink">{provider.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink/50">
                {provider.category}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="drawer-close-btn"
            className="w-9 h-9 grid place-items-center rounded-full hover:bg-black/5"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <p className="text-sm text-ink/70 leading-relaxed">{provider.description}</p>

          {provider.docs && (
            <a
              href={provider.docs}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-electric hover:underline"
            >
              Documentation <ExternalLink size={12} />
            </a>
          )}

          {/* Enable toggle */}
          <label className="flex items-center gap-3 p-4 rounded-xl border border-black/10 cursor-pointer hover:bg-black/[0.02]">
            <input
              type="checkbox"
              data-testid="drawer-enable-toggle"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-5 h-5 accent-electric"
            />
            <div>
              <div className="font-display font-bold text-sm text-ink">
                Enable {provider.name}
              </div>
              <div className="text-xs text-ink/60">
                When on, events will be sent automatically on new orders.
              </div>
            </div>
          </label>

          {/* Fields */}
          <div className="space-y-4">
            {provider.fields.map((f) => {
              const val = config[f.key] ?? "";
              if (f.type === "readonly") {
                return (
                  <div key={f.key}>
                    <div className="text-xs font-mono uppercase tracking-widest text-ink/60 mb-1.5">
                      {f.label}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={val}
                        data-testid={`drawer-field-${f.key}`}
                        className="flex-1 px-3 h-11 rounded-lg border border-black/10 bg-black/[0.03] font-mono text-xs text-ink"
                      />
                      <button
                        type="button"
                        onClick={() => copy(val)}
                        className="w-11 h-11 grid place-items-center rounded-lg border border-black/10 hover:bg-black/5"
                        title="Copy URL"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                );
              }
              const isSecret = f.type === "secret";
              return (
                <div key={f.key}>
                  <label className="text-xs font-mono uppercase tracking-widest text-ink/60 mb-1.5 block">
                    {f.label}
                  </label>
                  <div className="relative">
                    <input
                      type={isSecret && !revealed[f.key] ? "password" : "text"}
                      value={val}
                      placeholder={f.placeholder || ""}
                      data-testid={`drawer-field-${f.key}`}
                      onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
                      className="w-full px-3 h-11 rounded-lg border border-black/10 focus:border-electric focus:ring-2 focus:ring-electric/20 outline-none text-sm"
                    />
                    {isSecret && (
                      <button
                        type="button"
                        onClick={() =>
                          setRevealed({ ...revealed, [f.key]: !revealed[f.key] })
                        }
                        className="absolute end-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-md hover:bg-black/5"
                      >
                        {revealed[f.key] ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="drawer-save-btn"
              className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-ink text-white text-xs font-mono uppercase tracking-widest hover:bg-electric transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Save
            </button>
            {provider.server_side && (provider.fires_on || []).includes("purchase") && (
              <button
                type="button"
                onClick={runTest}
                disabled={testing}
                data-testid="drawer-test-btn"
                className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-electric text-white text-xs font-mono uppercase tracking-widest hover:bg-ink transition-colors disabled:opacity-60"
              >
                {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send test event
              </button>
            )}
          </div>

          {/* Event log */}
          {events.length > 0 && (
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-ink/60 mb-3">
                Recent events (last {events.length})
              </div>
              <div className="rounded-xl border border-black/10 overflow-hidden">
                {events.map((ev) => (
                  <div
                    key={ev.id}
                    className="px-4 py-3 border-t border-black/5 first:border-t-0 flex items-start gap-3 text-xs"
                  >
                    {ev.status === "success" ? (
                      <Check size={14} className="text-emerald-600 mt-0.5" />
                    ) : (
                      <AlertTriangle size={14} className="text-rose-600 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono uppercase tracking-widest text-[10px] text-ink/60">
                          {ev.event}
                        </span>
                        <span className="text-[10px] text-ink/40">
                          {new Date(ev.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-1 text-ink/70 break-words">{ev.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-black/10 flex items-start gap-2 text-[11px] text-ink/50">
            <Shield size={12} className="mt-0.5" />
            <span>
              Secrets are stored server-side and never exposed in the browser bundle.
              Only public pixel IDs are injected into the storefront.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminIntegrations() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProvider, setActiveProvider] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations");
      setProviders(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openProvider = (key) => {
    setActiveProvider(providers.find((p) => p.provider === key) || null);
  };

  const closeDrawer = () => setActiveProvider(null);

  const enabledCount = providers.filter((p) => p.enabled).length;

  return (
    <div data-testid="admin-integrations">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-electric">
            <Zap size={12} /> Marketing & data
          </div>
          <h1 className="mt-2 font-display font-black text-3xl text-ink">
            Integrations
          </h1>
          <p className="mt-2 text-sm text-ink/60 max-w-2xl">
            Plug in your marketing and analytics stack. Purchase events fire
            automatically on every new order — no code required.
          </p>
        </div>
        <div className="text-end">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink/40">
            Active
          </div>
          <div className="font-display font-black text-3xl text-ink">
            {enabledCount}
            <span className="text-ink/30 text-xl"> / {providers.length}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-24 text-ink/40">
          <Loader2 size={24} className="animate-spin" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {providers.map((p) => (
            <ProviderCard key={p.provider} p={p} onOpen={openProvider} />
          ))}
        </div>
      )}

      <ConfigDrawer
        provider={activeProvider}
        onClose={closeDrawer}
        onSaved={() => { closeDrawer(); load(); }}
      />
    </div>
  );
}
